import type { CSSProperties } from 'react';
import { Button, Check } from '../../modules/design-system';

export interface MyPacksToolbarProps {
  totalSelectable: number;
  selectedCount: number;
  onSelectAll: () => void;
  onClear: () => void;
  onShip: () => void;
  onSubmit: () => void;
}

/**
 * Toolbar with two responsive layouts:
 *   • Mobile (Figma 278:7388) — sticky bottom bar pinned to the viewport,
 *     two equal-width pills: glass-frosted "Select All" + primary
 *     "Buyback All".
 *   • ≥600px (Figma 706:1817) — inline row on the right side of the
 *     stats bar with Select All checkbox + Ship glass pill + Buyback
 *     primary CTA.
 */
export function MyPacksToolbar({
  totalSelectable,
  selectedCount,
  onSelectAll,
  onClear,
  onShip,
  onSubmit,
}: MyPacksToolbarProps) {
  const allSelected = totalSelectable > 0 && selectedCount === totalSelectable;
  const someSelected = selectedCount > 0;

  return (
    <>
      {/* Desktop / wide tablet — inline row inside the stats bar. */}
      <div className="hidden @[600px]:flex items-center justify-end gap-[10px] flex-wrap @[400px]:flex-nowrap">
        <button
          type="button"
          onClick={allSelected ? onClear : onSelectAll}
          disabled={totalSelectable === 0}
          className="
            inline-flex items-center gap-2 text-white
            transition-opacity hover:opacity-90
            disabled:cursor-not-allowed disabled:opacity-40
          "
        >
          <span
            aria-hidden
            className="inline-flex size-[20px] items-center justify-center rounded-[7px] transition-colors"
            style={{
              background: allSelected
                ? 'var(--co-MyPacksToolbarSubmit-fill, #23D11D)'
                : 'transparent',
              border: '1px solid rgba(222,222,224,0.29)',
            }}
          >
            {allSelected && <Check size={14} strokeWidth={3} />}
          </span>
          <span
            className="font-semibold whitespace-nowrap"
            style={{ fontSize: '13px', lineHeight: '20px' }}
          >
            Select All
          </span>
        </button>

        <span
          aria-hidden
          className="inline-block h-[20px] w-px shrink-0"
          style={{ background: '#EAEAEB', opacity: 0.1 }}
        />

        <Button
          variant="glass-frosted"
          size="md"
          onClick={onShip}
          disabled={!someSelected}
          style={{ '--co-Button-glass-frostedBorderWidth': 0 } as CSSProperties}
        >
          Ship
        </Button>

        <Button
          variant="primary"
          size="md"
          onClick={onSubmit}
          disabled={!someSelected}
        >
          Accept Buyback
        </Button>
      </div>

      {/* Mobile — fixed bottom bar with two equal-width pills. */}
      <div
        className="
          @[600px]:hidden fixed bottom-0 left-0 right-0 z-30
          flex items-center gap-3 px-4 pt-3 pb-4
        "
        style={{
          background: 'rgba(0,0,0,0.45)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderTop: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <div className="flex-1">
          <Button
            variant="glass-frosted"
            size="lg"
            fullWidth
            onClick={allSelected ? onClear : onSelectAll}
            disabled={totalSelectable === 0}
          >
            {allSelected ? 'Clear' : 'Select All'}
          </Button>
        </div>
        <div className="flex-1">
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={onSubmit}
            disabled={!someSelected}
          >
            Buyback All
          </Button>
        </div>
      </div>
    </>
  );
}
